import { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { adminAPI, categoryAPI } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { ScrollArea } from '../components/ui/scroll-area';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Users,
  Buildings,
  CurrencyDollar,
  ChartLine,
  MagnifyingGlass,
  Plus,
  Package,
  FileText,
  Star,
  CircleNotch,
  CaretUp,
  CaretDown,
  Funnel,
  Export,
  Warning,
  Crown,
  ArrowClockwise,
  TrendUp,
  Calendar,
} from '@phosphor-icons/react';
import { toast } from 'sonner';

const AdminDashboardPage = () => {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [categories, setCategories] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [subscribers, setSubscribers] = useState([]);
  const [subscribersLastUpdated, setSubscribersLastUpdated] = useState(null);
  const [subscribersFilter, setSubscribersFilter] = useState('active');
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [roleFilter, setRoleFilter] = useState('all');
  const [newCategoryName, setNewCategoryName] = useState('');
  const [showNewCategoryDialog, setShowNewCategoryDialog] = useState(false);
  const [creatingCategory, setCreatingCategory] = useState(false);
  const [refreshingPrices, setRefreshingPrices] = useState(false);

  useEffect(() => {
    if (user?.role === 'admin') {
      fetchAllData();
    }
  }, [user]);

  // Auto-refresh subscribers list every 30 seconds
  useEffect(() => {
    if (user?.role !== 'admin') return;
    fetchSubscribers(subscribersFilter);
    const t = setInterval(() => fetchSubscribers(subscribersFilter), 30_000);
    return () => clearInterval(t);
  }, [user, subscribersFilter]);

  const fetchSubscribers = async (filter) => {
    try {
      const res = await adminAPI.getSubscribers(filter);
      setSubscribers(res.data.subscribers || []);
      setSubscribersLastUpdated(new Date());
    } catch (e) {
      console.error('Failed to load subscribers:', e);
    }
  };

  const handleRefreshPrices = async () => {
    if (refreshingPrices) return;
    if (!window.confirm('¿Insertar snapshots frescos de precios para todos los materiales (fecha de hoy o hasta 7 días atrás)? Esta acción es aditiva y no borra el histórico.')) {
      return;
    }
    setRefreshingPrices(true);
    try {
      const res = await adminAPI.refreshPriceDates({ days_back: 7, variation_pct: 3.0 });
      const s = res.data;
      toast.success(
        `Precios actualizados: ${s.records_inserted} snapshots (${s.unique_tuples_scanned} combinaciones · duración ${s.duration_seconds}s)`,
        { duration: 6000 },
      );
      // Refresh stats so counters are current
      const statsRes = await adminAPI.getStats();
      setStats(statsRes.data);
    } catch (e) {
      toast.error('Error al refrescar precios: ' + (e.response?.data?.detail || e.message));
    } finally {
      setRefreshingPrices(false);
    }
  };

  const fetchAllData = async () => {
    setLoading(true);
    try {
      const [statsRes, usersRes, categoriesRes, transactionsRes] = await Promise.all([
        adminAPI.getStats(),
        adminAPI.getUsers({ limit: 1000 }),
        categoryAPI.getAll(),
        adminAPI.getTransactions(),
      ]);
      setStats(statsRes.data);
      // Handle paginated users response
      setUsers(usersRes.data.users || usersRes.data || []);
      setCategories(categoriesRes.data || []);
      // Handle paginated transactions response
      setTransactions(transactionsRes.data.transactions || transactionsRes.data || []);
    } catch (error) {
      console.error('Failed to fetch admin data:', error);
      toast.error('Error al cargar datos de administrador');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateCategory = async () => {
    if (!newCategoryName.trim()) {
      toast.error('El nombre de la categoría es requerido');
      return;
    }
    setCreatingCategory(true);
    try {
      await categoryAPI.create({ name: newCategoryName.trim() });
      toast.success('Categoría creada exitosamente');
      setNewCategoryName('');
      setShowNewCategoryDialog(false);
      const categoriesRes = await categoryAPI.getAll();
      setCategories(categoriesRes.data);
    } catch (error) {
      toast.error('Error al crear categoría');
    } finally {
      setCreatingCategory(false);
    }
  };

  const filteredUsers = users.filter(u => {
    const matchesSearch = u.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
                          u.email?.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesRole = roleFilter === 'all' || u.role === roleFilter;
    return matchesSearch && matchesRole;
  });

  const getRoleBadge = (role) => {
    const roleStyles = {
      cliente: 'bg-blue-100 text-blue-700 border-blue-300',
      proveedor: 'bg-green-100 text-green-700 border-green-300',
      ambos: 'bg-purple-100 text-purple-700 border-purple-300',
      admin: 'bg-orange-100 text-orange-700 border-orange-300',
    };
    return (
      <Badge variant="outline" className={`rounded-none capitalize ${roleStyles[role] || ''}`}>
        {role}
      </Badge>
    );
  };

  if (user?.role !== 'admin') {
    return (
      <div className="text-center py-20">
        <Warning className="w-16 h-16 text-red-500 mx-auto mb-4" />
        <h2 className="text-xl font-bold text-zinc-900">Acceso Denegado</h2>
        <p className="text-zinc-600">Solo los administradores pueden acceder a esta página</p>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <CircleNotch className="w-8 h-8 text-orange-500 animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="admin-dashboard-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-zinc-900">Panel de Administración</h1>
          <p className="text-zinc-600">Gestiona usuarios, categorías y monitorea la plataforma</p>
        </div>
        <Badge className="bg-orange-500 text-white rounded-none">Admin</Badge>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-4" data-testid="admin-stats-grid">
        <Card className="border-zinc-200 rounded-none">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-zinc-500 uppercase tracking-wide">Usuarios Totales</p>
                <p className="text-3xl font-bold text-zinc-900 mt-1">{stats?.users?.total || 0}</p>
                <div className="flex items-center gap-2 mt-2 text-xs">
                  <span className="text-blue-600">{stats?.users?.clients || 0} clientes</span>
                  <span className="text-green-600">{stats?.users?.providers || 0} proveedores</span>
                </div>
              </div>
              <div className="w-12 h-12 bg-blue-100 flex items-center justify-center">
                <Users className="w-6 h-6 text-blue-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-zinc-200 rounded-none">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-zinc-500 uppercase tracking-wide">Proyectos</p>
                <p className="text-3xl font-bold text-zinc-900 mt-1">{stats?.projects?.total || 0}</p>
                <p className="text-xs text-green-600 mt-2">{stats?.projects?.sent || 0} enviados</p>
              </div>
              <div className="w-12 h-12 bg-orange-100 flex items-center justify-center">
                <Buildings className="w-6 h-6 text-orange-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-zinc-200 rounded-none">
          <CardContent className="p-6">
            <div className="flex items-center justify-between gap-3">
              <div className="min-w-0">
                <p className="text-sm text-zinc-500 uppercase tracking-wide">Ingresos</p>
                <p className="text-2xl xl:text-[1.6rem] font-bold text-zinc-900 mt-1 truncate" data-testid="admin-revenue-total">
                  ${Math.round(stats?.revenue?.total || 0).toLocaleString('es-MX')}
                </p>
                <p className="text-xs text-zinc-500 mt-2">
                  {stats?.revenue?.transaction_count || 0} transacciones
                </p>
              </div>
              <div className="w-12 h-12 bg-green-100 flex items-center justify-center flex-shrink-0">
                <CurrencyDollar className="w-6 h-6 text-green-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-zinc-200 rounded-none">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-zinc-500 uppercase tracking-wide">Propuestas</p>
                <p className="text-3xl font-bold text-zinc-900 mt-1">{stats?.proposals?.total || 0}</p>
                <p className="text-xs text-green-600 mt-2">{stats?.proposals?.accepted || 0} aceptadas</p>
              </div>
              <div className="w-12 h-12 bg-purple-100 flex items-center justify-center">
                <FileText className="w-6 h-6 text-purple-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-zinc-200 rounded-none" data-testid="admin-subscriptions-card">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div className="min-w-0">
                <p className="text-sm text-zinc-500 uppercase tracking-wide">Suscripciones</p>
                <p
                  className="text-3xl font-bold text-zinc-900 mt-1"
                  data-testid="admin-subscriptions-active-count"
                >
                  {stats?.subscriptions?.active || 0}
                </p>
                <p className="text-xs text-zinc-500 mt-2">Consulta de precios · activas</p>
                <div className="flex flex-wrap items-center gap-x-3 gap-y-1 mt-2 text-[11px] leading-tight">
                  <span className="text-amber-600" data-testid="admin-subs-monthly">
                    {stats?.subscriptions?.by_plan?.monthly || 0} mensual
                  </span>
                  <span className="text-blue-600" data-testid="admin-subs-semestral">
                    {stats?.subscriptions?.by_plan?.semestral || 0} semestral
                  </span>
                  <span className="text-green-600" data-testid="admin-subs-annual">
                    {stats?.subscriptions?.by_plan?.annual || 0} anual
                  </span>
                </div>
                {(stats?.subscriptions?.queued || 0) > 0 && (
                  <p className="text-[11px] text-zinc-400 mt-1" data-testid="admin-subs-queued">
                    +{stats.subscriptions.queued} en cola
                  </p>
                )}
              </div>
              <div className="w-12 h-12 bg-amber-100 flex items-center justify-center shrink-0">
                <Crown className="w-6 h-6 text-amber-600" weight="fill" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Tabs */}
      <Tabs defaultValue="users" className="space-y-6">
        <TabsList className="bg-zinc-100 rounded-none p-1 border border-zinc-200">
          <TabsTrigger value="users" className="rounded-none data-[state=active]:bg-white">
            <Users className="w-4 h-4 mr-2" />
            Usuarios ({users.length})
          </TabsTrigger>
          <TabsTrigger value="categories" className="rounded-none data-[state=active]:bg-white">
            <Package className="w-4 h-4 mr-2" />
            Categorías ({categories.length})
          </TabsTrigger>
          <TabsTrigger value="transactions" className="rounded-none data-[state=active]:bg-white">
            <CurrencyDollar className="w-4 h-4 mr-2" />
            Transacciones
          </TabsTrigger>
          <TabsTrigger
            value="subscribers"
            className="rounded-none data-[state=active]:bg-white"
            data-testid="admin-tab-subscribers"
          >
            <Crown className="w-4 h-4 mr-2" weight="fill" />
            Suscriptores ({subscribers.length})
          </TabsTrigger>
        </TabsList>

        {/* Users Tab */}
        <TabsContent value="users">
          <Card className="border-zinc-200 rounded-none">
            <CardHeader className="border-b border-zinc-200">
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                <CardTitle>Gestión de Usuarios</CardTitle>
                <div className="flex gap-2">
                  <div className="relative">
                    <MagnifyingGlass className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-400" />
                    <Input
                      placeholder="Buscar usuarios..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      className="pl-9 rounded-none w-64"
                      data-testid="search-users"
                    />
                  </div>
                  <Select value={roleFilter} onValueChange={setRoleFilter}>
                    <SelectTrigger className="w-40 rounded-none">
                      <Funnel className="w-4 h-4 mr-2" />
                      <SelectValue placeholder="Filtrar" />
                    </SelectTrigger>
                    <SelectContent className="rounded-none">
                      <SelectItem value="all">Todos</SelectItem>
                      <SelectItem value="cliente">Clientes</SelectItem>
                      <SelectItem value="proveedor">Proveedores</SelectItem>
                      <SelectItem value="ambos">Ambos</SelectItem>
                      <SelectItem value="admin">Admins</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </CardHeader>
            <CardContent className="p-0">
              <ScrollArea className="h-[400px]">
                <table className="w-full data-table">
                  <thead className="sticky top-0 bg-white">
                    <tr>
                      <th>Usuario</th>
                      <th>Email</th>
                      <th>Rol</th>
                      <th className="text-center">Calificación</th>
                      <th>Registro</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredUsers.map((u) => (
                      <tr key={u.user_id} data-testid={`user-row-${u.user_id}`}>
                        <td className="font-medium">{u.name || 'Sin nombre'}</td>
                        <td className="text-zinc-600">{u.email}</td>
                        <td>{getRoleBadge(u.role)}</td>
                        <td className="text-center">
                          <div className="flex items-center justify-center gap-1">
                            <Star className="w-4 h-4 text-amber-500" weight="fill" />
                            <span>{u.rating_avg?.toFixed(1) || '5.0'}</span>
                          </div>
                        </td>
                        <td className="text-zinc-500 text-sm">
                          {new Date(u.created_at).toLocaleDateString('es-MX')}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </ScrollArea>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Categories Tab */}
        <TabsContent value="categories">
          <Card className="border-zinc-200 rounded-none">
            <CardHeader className="border-b border-zinc-200">
              <div className="flex items-center justify-between">
                <CardTitle>Categorías de Materiales</CardTitle>
                <Dialog open={showNewCategoryDialog} onOpenChange={setShowNewCategoryDialog}>
                  <DialogTrigger asChild>
                    <Button className="btn-primary rounded-none" data-testid="add-category-btn">
                      <Plus className="w-4 h-4 mr-2" />
                      Nueva Categoría
                    </Button>
                  </DialogTrigger>
                  <DialogContent className="rounded-none">
                    <DialogHeader>
                      <DialogTitle>Crear Nueva Categoría</DialogTitle>
                      <DialogDescription>
                        Las categorías organizan los materiales y ayudan a los proveedores a encontrar cotizaciones relevantes.
                      </DialogDescription>
                    </DialogHeader>
                    <div className="py-4">
                      <Label htmlFor="categoryName">Nombre de la Categoría</Label>
                      <Input
                        id="categoryName"
                        value={newCategoryName}
                        onChange={(e) => setNewCategoryName(e.target.value)}
                        placeholder="Ej: Materiales eléctricos"
                        className="rounded-none mt-2"
                        data-testid="new-category-input"
                      />
                    </div>
                    <DialogFooter>
                      <Button 
                        variant="outline" 
                        onClick={() => setShowNewCategoryDialog(false)}
                        className="rounded-none"
                      >
                        Cancelar
                      </Button>
                      <Button 
                        onClick={handleCreateCategory}
                        disabled={creatingCategory}
                        className="btn-primary rounded-none"
                        data-testid="create-category-btn"
                      >
                        {creatingCategory ? <CircleNotch className="w-4 h-4 animate-spin" /> : 'Crear'}
                      </Button>
                    </DialogFooter>
                  </DialogContent>
                </Dialog>
              </div>
            </CardHeader>
            <CardContent className="p-0">
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 p-4">
                {categories.map((category, index) => (
                  <div 
                    key={category.category_id} 
                    className="flex items-center justify-between p-4 border border-zinc-200 hover:bg-zinc-50"
                    data-testid={`category-${category.category_id}`}
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 bg-amber-100 flex items-center justify-center text-amber-700 font-bold">
                        {index + 1}
                      </div>
                      <span className="font-medium">{category.name}</span>
                    </div>
                    <Badge variant="outline" className="rounded-none text-xs">
                      {category.status || 'active'}
                    </Badge>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Transactions Tab */}
        <TabsContent value="transactions">
          <Card className="border-zinc-200 rounded-none">
            <CardHeader className="border-b border-zinc-200">
              <div className="flex items-center justify-between">
                <CardTitle>Historial de Transacciones</CardTitle>
                <Button variant="outline" className="rounded-none">
                  <Export className="w-4 h-4 mr-2" />
                  Exportar
                </Button>
              </div>
            </CardHeader>
            <CardContent className="p-0">
              <ScrollArea className="h-[400px]">
                {transactions.length === 0 ? (
                  <div className="p-8 text-center text-zinc-500">
                    No hay transacciones registradas
                  </div>
                ) : (
                  <table className="w-full data-table">
                    <thead className="sticky top-0 bg-white">
                      <tr>
                        <th>ID</th>
                        <th>Tipo</th>
                        <th>Usuario</th>
                        <th className="text-right">Monto</th>
                        <th>Estado</th>
                        <th>Fecha</th>
                      </tr>
                    </thead>
                    <tbody>
                      {transactions.map((tx) => (
                        <tr key={tx.transaction_id}>
                          <td className="font-mono text-xs">{tx.transaction_id?.slice(-8)}</td>
                          <td className="capitalize">{tx.type}</td>
                          <td>{tx.user_name || tx.user_id?.slice(-8)}</td>
                          <td className="text-right font-medium">
                            ${tx.amount?.toLocaleString('es-MX', { minimumFractionDigits: 2 })}
                          </td>
                          <td>
                            <Badge 
                              variant="outline" 
                              className={`rounded-none ${
                                tx.status === 'completed' ? 'bg-green-100 text-green-700' :
                                tx.status === 'pending' ? 'bg-amber-100 text-amber-700' :
                                'bg-red-100 text-red-700'
                              }`}
                            >
                              {tx.status}
                            </Badge>
                          </td>
                          <td className="text-zinc-500 text-sm">
                            {new Date(tx.created_at).toLocaleDateString('es-MX')}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </ScrollArea>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Subscribers Tab — Consultor de Precios */}
        <TabsContent value="subscribers">
          <Card className="border-zinc-200 rounded-none" data-testid="admin-subscribers-panel">
            <CardHeader className="border-b border-zinc-200">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                <div>
                  <CardTitle className="flex items-center gap-2 text-base">
                    <Crown className="w-5 h-5 text-amber-600" weight="fill" />
                    Suscriptores del Consultor de Precios
                  </CardTitle>
                  <p className="text-xs text-zinc-500 mt-1">
                    {subscribersLastUpdated && (
                      <>Última actualización: {subscribersLastUpdated.toLocaleTimeString('es-MX')} · </>
                    )}
                    Auto-refresh cada 30s
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  <Select value={subscribersFilter} onValueChange={setSubscribersFilter}>
                    <SelectTrigger
                      className="w-40 rounded-none h-9"
                      data-testid="admin-subscribers-filter"
                    >
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="active">Activas</SelectItem>
                      <SelectItem value="queued">En cola</SelectItem>
                      <SelectItem value="expired">Vencidas</SelectItem>
                      <SelectItem value="all">Todas</SelectItem>
                    </SelectContent>
                  </Select>
                  <Button
                    variant="outline"
                    className="rounded-none h-9"
                    onClick={() => fetchSubscribers(subscribersFilter)}
                    data-testid="admin-subscribers-refresh-btn"
                  >
                    <ArrowClockwise className="w-4 h-4 mr-2" /> Refrescar
                  </Button>
                  <Button
                    className="rounded-none h-9 bg-orange-500 hover:bg-orange-600"
                    onClick={handleRefreshPrices}
                    disabled={refreshingPrices}
                    data-testid="admin-refresh-prices-btn"
                    title="Inserta snapshots frescos de precios con fechas de los últimos 7 días para todas las combinaciones (material, estado, marca). No borra histórico."
                  >
                    {refreshingPrices ? (
                      <><CircleNotch className="w-4 h-4 mr-2 animate-spin" /> Actualizando…</>
                    ) : (
                      <><TrendUp className="w-4 h-4 mr-2" weight="bold" /> Actualizar Precios</>
                    )}
                  </Button>
                </div>
              </div>
            </CardHeader>
            <CardContent className="p-0">
              <ScrollArea className="h-[560px]">
                {subscribers.length === 0 ? (
                  <div className="p-10 text-center text-zinc-500 text-sm">
                    No hay suscriptores en esta categoría.
                  </div>
                ) : (
                  <table className="w-full text-sm" data-testid="admin-subscribers-table">
                    <thead className="bg-zinc-50 sticky top-0 z-10">
                      <tr className="text-left text-xs uppercase text-zinc-500 tracking-wide">
                        <th className="px-4 py-3">Usuario</th>
                        <th className="px-4 py-3">Correo</th>
                        <th className="px-4 py-3">Rol</th>
                        <th className="px-4 py-3">Plan</th>
                        <th className="px-4 py-3">Estado</th>
                        <th className="px-4 py-3">Vence</th>
                        <th className="px-4 py-3 text-right">Días</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-zinc-100">
                      {subscribers.map((s) => {
                        const statusColor = s.status === 'active'
                          ? 'bg-green-100 text-green-700'
                          : s.status === 'queued'
                            ? 'bg-blue-100 text-blue-700'
                            : 'bg-zinc-100 text-zinc-600';
                        const daysColor = s.days_remaining == null
                          ? 'text-zinc-400'
                          : s.days_remaining < 7
                            ? 'text-red-600 font-semibold'
                            : s.days_remaining < 30
                              ? 'text-amber-600'
                              : 'text-zinc-700';
                        return (
                          <tr
                            key={s.subscription_id}
                            className="hover:bg-zinc-50"
                            data-testid={`subscriber-row-${s.subscription_id}`}
                          >
                            <td className="px-4 py-3">
                              <div className="font-medium text-zinc-900">{s.name || '—'}</div>
                              {s.business_name && (
                                <div className="text-xs text-zinc-500">{s.business_name}</div>
                              )}
                            </td>
                            <td className="px-4 py-3 text-zinc-700 font-mono text-xs">{s.email}</td>
                            <td className="px-4 py-3">
                              <span className="text-xs text-zinc-600 capitalize">{s.role || '—'}</span>
                            </td>
                            <td className="px-4 py-3">
                              <div className="font-medium text-zinc-800">{s.plan_label}</div>
                              <div className="text-sm font-semibold text-zinc-900" data-testid={`admin-sub-price-iva-${s.subscription_id || s.user_id}`}>
                                ${(s.plan_price_with_iva || 0).toLocaleString('es-MX', { minimumFractionDigits: 2 })}
                                <span className="text-[10px] font-normal text-zinc-500 ml-1">IVA incl.</span>
                              </div>
                              <div className="text-xs text-zinc-500" data-testid={`admin-sub-price-base-${s.subscription_id || s.user_id}`}>
                                ${(s.plan_price || 0).toLocaleString('es-MX', { minimumFractionDigits: 2 })} + IVA
                              </div>
                            </td>
                            <td className="px-4 py-3">
                              <Badge className={`${statusColor} rounded-none text-xs`}>
                                {s.status === 'active' ? 'Activa' : s.status === 'queued' ? 'En cola' : 'Vencida'}
                              </Badge>
                            </td>
                            <td className="px-4 py-3 text-zinc-600">
                              <div className="flex items-center gap-1">
                                <Calendar className="w-3 h-3 text-zinc-400" />
                                {s.end_date ? new Date(s.end_date).toLocaleDateString('es-MX', { day: '2-digit', month: 'short', year: 'numeric' }) : '—'}
                              </div>
                            </td>
                            <td className={`px-4 py-3 text-right ${daysColor}`}>
                              {s.days_remaining != null ? `${s.days_remaining}d` : '—'}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                )}
              </ScrollArea>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default AdminDashboardPage;
